<template>
    <div ref="carouselRoot" class="overflow-hidden w-full">
        <div ref="trackRef" class="flex w-max gap-10">
            <div ref="setRef" class="flex w-max gap-10 border border-workbook-blue/0">
                
                <template v-for="image in resolvedImages" :key="image.src">
                    <TwicImg
                        @click="wisePopup"
                        class="inline-block"
                        :style="intrinsicStyle"
                        :src="image.src"
                        :alt="image.alt"
                        :ratio="props.proportions"
                        :intrinsic="props.intrinsic"
                        mode="contain"
                        placeholder="none"
                    />
                </template>
            </div>
            <div class="flex w-max gap-10" aria-hidden="true">
                <template v-for="(image, index) in resolvedImages" :key="`${image.src}-${index}-clone`">
                    <TwicImg
                        class="inline-block"
                        :style="intrinsicStyle"
                        :src="image.src"
                        alt=""
                        :ratio="props.proportions"
                        :intrinsic="props.intrinsic"
                        mode="contain"
                        placeholder="none"
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type gsap from "gsap";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useIntersectionObserver, useResizeObserver } from "@vueuse/core";
import { useRuntimeConfig } from "#app";
import { toTwicpicUrl } from "@/utils/productImages";
import { loadGsap } from "@/utils/gsap";



const wisePopup = () => {
    if (typeof window === 'undefined') return
    const windowWithWisepops = window as typeof window & { wisepops?: ((...args: unknown[]) => void) }
    windowWithWisepops.wisepops?.('event', 'case_study_clicked')
  
}

type CarouselImage = {
    src: string;
    alt: string;
};

const props = withDefaults(
    defineProps<{
        folder: string;
        slides: number;
        proportions: string;
        intrinsic: string;
        speed?: number;
    }>(),
    {
        speed: 20,
    },
);

const config = useRuntimeConfig();

const normalizedFolder = computed(() => props.folder.replace(/^\/+|\/+$/g, ""));
const deploymentSuffix = computed(() =>
    config.public.appVersion ? `?deployment=${config.public.appVersion}` : "",
);
const isDevMode = import.meta.dev;
const intrinsicStyle = computed(() => {
    const [width, height] = props.intrinsic.split("x").map(Number);
    if (!width || !height) {
        return undefined;
    }
    return {
        width: `${width}px`,
        height: `${height}px`,
    };
});

const resolvedImages = computed<CarouselImage[]>(() => {
    if (!normalizedFolder.value || props.slides <= 0) {
        return [];
    }

    return Array.from({ length: props.slides }, (_, index) => {
        const slideNumber = String(index + 1).padStart(2, "0");
        const baseSrc = `/${normalizedFolder.value}/slide${slideNumber}.jpg${deploymentSuffix.value}`;
        return {
            src: toTwicpicUrl(baseSrc, false),
            alt: `Slide ${index + 1}`,
        };
    });
});

const carouselRoot = ref<HTMLElement | null>(null);
const trackRef = ref<HTMLElement | null>(null);
const setRef = ref<HTMLElement | null>(null);

let tween: gsap.core.Tween | null = null;
let isInView = false;
let gsapInstance: typeof gsap | null = null;

const ensureGsap = async () => {
    if (!gsapInstance) {
        const { gsap } = await loadGsap();
        gsapInstance = gsap;
    }
    return gsapInstance;
};

const setupAnimation = async () => {
    if (!trackRef.value || !setRef.value) {
        return;
    }

    const totalWidth = setRef.value.scrollWidth;
    if (!totalWidth) {
        return;
    }

    const gsap = await ensureGsap();
    tween?.kill();
    trackRef.value.style.transform = "translateX(0)";
    tween = gsap.to(trackRef.value, {
        x: -totalWidth,
        duration: totalWidth / props.speed,
        ease: "none",
        repeat: -1,
    });

    if (!isInView) {
        tween.pause();
    }
};

const startWhenVisible = ([entry]: IntersectionObserverEntry[]) => {
    isInView = entry.isIntersecting;

    if (isInView && !tween) {
        void setupAnimation();
        return;
    }

    if (tween) {
        if (isInView) {
            tween.play();
        } else {
            tween.pause();
        }
    }
};

const { stop: stopIntersectionObserver } = useIntersectionObserver(
    carouselRoot,
    startWhenVisible,
    { threshold: 0.1 },
);

const { stop: stopResizeObserver } = useResizeObserver(setRef, () => {
    void setupAnimation();
});

onMounted(async () => {
    await nextTick();
    void setupAnimation();
});

watch(
    resolvedImages,
    async () => {
        await nextTick();
        void setupAnimation();
    },
    { deep: true },
);

onBeforeUnmount(() => {
    tween?.kill();
    stopIntersectionObserver();
    stopResizeObserver();
});
</script>
